import type { CoverageTreatmentAttribution } from "@redotech/redo-ab-testing-common/coverage-treatment-id";
import { coverageAttributionFromMongoTreatmentId } from "@redotech/redo-ab-testing-common/coverage-treatment-id";
import type { CoverageWidgetRenderedPayload } from "@redotech/redo-customer-event-common/events/coverage-widget-rendered";
import { fnv1a32Hex } from "@redotech/redo-storefront-common/cart-hash";
import {
  CART_PLACEMENT_CONTAINER_TYPES,
  type CartPlacementContainerType,
} from "@redotech/redo-storefront-common/widget-config";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import { getShopifySessionId } from "@redotech/redo-web/utils/shopify-session";
import { checkoutPlacementContainerTypeForElement } from "../coverage-builder/checkout-cart-surface";
import { RedoEmittedDomEvent } from "../dom-events";
import { log } from "../monitoring/log";
import { getStoredTreatmentId } from "../utils";
import { isElementVisible } from "./is-element-visible";
import {
  dispatchStorefrontCoverageEvent,
  readEligibleAnonymousId,
} from "./storefront-coverage-event";

const RENDERED_FLAG_KEY_PREFIX = "redo_cart_widget_rendered:";
const RENDERED_FLAG_EXPIRY_HOURS = 24;

function isShopifyStorefront(): boolean {
  return globalThis.redoStorefront?.type === "shopify";
}

function cartSurfaceOf(
  element: Element,
): CartPlacementContainerType | undefined {
  const surface = checkoutPlacementContainerTypeForElement(element);
  return CART_PLACEMENT_CONTAINER_TYPES.find(
    (cartSurface) => cartSurface === surface,
  );
}

/** True the first time this session sees the key; sets the flag before the caller sends. */
function claimRenderedFlag(dedupKey: string): boolean {
  const flagKey = `${RENDERED_FLAG_KEY_PREFIX}${dedupKey}`;
  if (getLocalStorageWithExpiry(flagKey)) {
    return false;
  }
  setLocalStorageWithExpiry(flagKey, true, RENDERED_FLAG_EXPIRY_HOURS);
  return true;
}

interface SessionContext {
  anonymousId: string;
  sessionId: string;
  attribution: CoverageTreatmentAttribution;
}

/** Everything that is the same for all widgets of a render; undefined when nothing can be sent. */
function readSessionContext(): SessionContext | undefined {
  if (!isShopifyStorefront()) {
    return undefined;
  }
  const attribution = coverageAttributionFromMongoTreatmentId(
    getStoredTreatmentId(),
  );
  if (!attribution) {
    return undefined;
  }
  const sessionId = getShopifySessionId();
  const anonymousId = sessionId ? readEligibleAnonymousId() : undefined;
  return sessionId && anonymousId
    ? { anonymousId, sessionId, attribution }
    : undefined;
}

function sendOnce(
  { anonymousId, sessionId, attribution }: SessionContext,
  cartSurface: CartPlacementContainerType,
): void {
  const dedupKey = `${sessionId}:${attribution.coverageTreatmentId}:${cartSurface}`;
  if (!claimRenderedFlag(dedupKey)) {
    return;
  }
  dispatchStorefrontCoverageEvent<CoverageWidgetRenderedPayload>(
    RedoEmittedDomEvent.CoverageWidgetRendered,
    {
      anonymousId,
      sessionId,
      cartSurface,
      ...attribution,
      eventId: fnv1a32Hex(dedupKey),
    },
  );
}

function reportElement(element: Element, context: SessionContext): void {
  const cartSurface = cartSurfaceOf(element);
  if (cartSurface && element.isConnected && isElementVisible(element)) {
    sendOnce(context, cartSurface);
  }
}

/**
 * Reports the visibly rendered coverage widgets of one render that sit on a
 * cart surface, once per session, treatment and surface. The per-session
 * checks run first so merchants without a treatment pay almost nothing. Runs
 * in the render path, so it never throws.
 */
export function recordCoverageWidgetsRendered(
  elements: readonly Element[],
): void {
  try {
    const context = elements.length > 0 ? readSessionContext() : undefined;
    if (context) {
      for (const element of elements) {
        reportElement(element, context);
      }
    }
  } catch (error) {
    log("Error recording coverage widget rendered", error);
  }
}
